/* eslint-disable lingui/no-unlocalized-strings -- contratos técnicos HTTP */
import { creditApi } from '@/shared/http/client';
import { downloadFile } from '@/shared/http/download';
import { wire, withSignal } from '@/shared/reference/wire';
import {
  availableWalletsSchema,
  creditProductListPageSchema,
  creditProductOptionPageSchema,
  creditProductRowPageSchema,
  creditProductSchema,
  governmentDepartmentPageSchema,
  modalityPageSchema,
  productRulesPageSchema,
  ruleFieldListSchema,
  savedSchema,
  signatureTemplateSchema,
  type AssignBody,
  type ShareBody,
  type TemplateBody,
} from './credit-product.wire';

/** `services/creditProduct/creditProduct.ts:16`: tira `undefined`, `''` e `' '`, mantém a ordem. */
function withoutEmpty(input: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined && value !== '' && value !== ' '),
  );
}

/** UYZY services/creditProduct/creditProduct.ts:15; contexts/creditProductContext.tsx:133. */
export async function listCreditProductOptions(
  options: {
    page?: number;
    size?: number;
    searchString?: string | undefined;
    tenant: string | undefined;
  },
  signal?: AbortSignal,
) {
  const input = {
    page: options.page ?? 0,
    size: options.size ?? 10,
    tenant: options.tenant,
    isConsiderPostingDeadline: true,
    searchString: options.searchString,
  };
  return wire(
    creditProductOptionPageSchema,
    await creditApi.get<unknown>('/Product', { params: withoutEmpty(input), ...withSignal(signal) }),
    'GET /Product',
  );
}

export interface CreditProductListParams {
  page?: number;
  size?: number;
  name?: string | undefined;
  amortizationType?: string | undefined;
  initialDate?: string | undefined;
  finalDate?: string | undefined;
  searchString?: string | undefined;
  tenant?: string | undefined;
  categoryName?: string | undefined;
  termStatus?: string | undefined;
  isConsiderPostingDeadline?: boolean | undefined;
}

/** Lista normal: mantém ordem dos filtros e não impõe prazo de postagem do autocomplete (U02). */
export async function listCreditProducts(options: CreditProductListParams, signal?: AbortSignal) {
  const input = { page: 0, size: 10, ...options };
  return wire(
    creditProductListPageSchema,
    await creditApi.get<unknown>('/Product', { params: withoutEmpty(input), ...withSignal(signal) }),
    'GET /Product',
  );
}

/** A mesma chamada da lista, com as colunas da tela (`CreditProductList.tsx:34-172`). */
export async function listCreditProductRows(options: CreditProductListParams, signal?: AbortSignal) {
  return wire(creditProductRowPageSchema, await listCreditProducts(options, signal), 'GET /Product');
}

/** `creditProduct.ts:147-162` + `creditProductContext.tsx:472-490`: página 1, 100 itens, binário. */
export async function exportCreditProducts(
  filters: Record<string, unknown>,
  tenant: string | undefined,
  fallbackFileName: string,
  signal?: AbortSignal,
) {
  const params = withoutEmpty({ page: 1, size: 100, ...filters, tenant });
  return downloadFile(
    creditApi,
    { method: 'GET', url: '/Product/ExportarExcelParametrizacoes', params, ...withSignal(signal) },
    { fallbackFileName },
  );
}

/** `creditProduct.ts:125`. */
export async function getCreditProduct(id: string, tenant: string | undefined, signal?: AbortSignal) {
  return wire(
    creditProductSchema,
    await creditApi.get<unknown>(`/Product/${id}`, { params: { tenant }, ...withSignal(signal) }),
    'GET /Product/{id}',
  );
}

/** `creditProduct.ts:87-110`: POST na criação, PUT na edição, sempre `returnValue=Full`. */
export async function saveCreditProduct(
  id: string | undefined,
  payload: Record<string, unknown>,
  tenant: string | undefined,
) {
  const config = { params: { tenant, returnValue: 'Full' } };
  const data = id
    ? await creditApi.put<unknown>(`/Product/${id}`, payload, config)
    : await creditApi.post('/Product', payload, config);
  return wire(savedSchema, data, 'SAVE /Product');
}

/** `creditProduct.ts:112-123` (`createCreditProduct`): o GET clonado vai inteiro, no tenant escolhido. */
export async function cloneCreditProduct(payload: Record<string, unknown>, tenant: string) {
  return wire(
    savedSchema,
    await creditApi.post('/Product', payload, { params: { tenant } }),
    'CLONE /Product',
  );
}

/** `CreditProductContainer.tsx:69` e `CreditProductFormContainer.tsx:233` chamam sem tenant. */
export async function deleteCreditProduct(id: string) {
  await creditApi.delete(`/Product/${id}`, { params: { tenant: undefined } });
}

/** `services/assign/index.ts:6-25`: `AssingCreateModel` com strings vazias. */
export async function assignCreditProduct(id: string, body: AssignBody) {
  await creditApi.post(`/Product/${id}/assign`, {
    userId: body.userId ?? '',
    groupName: body.groupName ?? '',
    tenant: body.tenant || undefined,
  });
}

/** `services/share/index.ts:4-15`, recurso `Product`. */
export async function shareCreditProduct(id: string, body: ShareBody, tenant: string | undefined) {
  await creditApi.post(
    `/Product/${id}/Share`,
    {
      permissionLevelType: body.permissionLevelType,
      principalId: body.principalId,
      principalType: body.principalType,
    },
    { params: { tenant } },
  );
}

/** `creditProduct.ts:29-41`. */
export async function listModalities(
  options: { page: number; size: number; descriptionModality?: string | undefined },
  signal?: AbortSignal,
) {
  return wire(
    modalityPageSchema,
    await creditApi.get<unknown>('/Product/GetModality', {
      params: withoutEmpty(options),
      ...withSignal(signal),
    }),
    'GET /Product/GetModality',
  );
}

/** `creditProduct.ts:43-55`; o contexto não chama sem `codeModalite` (`creditProductContext.tsx:360`). */
export async function listSubModalities(
  options: {
    page: number;
    size: number;
    codeModalite?: number | undefined;
    descriptionSubModality?: string | undefined;
  },
  signal?: AbortSignal,
) {
  return wire(
    modalityPageSchema,
    await creditApi.get<unknown>('/Product/GetSubModality', {
      params: withoutEmpty(options),
      ...withSignal(signal),
    }),
    'GET /Product/GetSubModality',
  );
}

/** `creditProduct.ts:70-84`; erro vira `null` (`creditProductContext.tsx:335-341`). */
export async function getAvailableWallets(
  params: {
    categoryName?: string | undefined;
    liquidationApprovalTenantName?: string | undefined;
    productId?: string | undefined;
  },
  signal?: AbortSignal,
) {
  try {
    return wire(
      availableWalletsSchema,
      await creditApi.get<unknown>('/Product/AvailableWallets', { params, ...withSignal(signal) }),
      'GET /Product/AvailableWallets',
    );
  } catch (error) {
    if (signal?.aborted) throw error;
    return null;
  }
}

/** `productRules/productRules.ts:11-17`; `rulesType` 1 compliance, 2 crédito, 3 dinâmica. */
export async function listProductRules(
  params: { name?: string | undefined; rulesType: 1 | 2 | 3 },
  signal?: AbortSignal,
) {
  return wire(
    productRulesPageSchema,
    await creditApi.get<unknown>('/ProductRules', { params, ...withSignal(signal) }),
    'GET /ProductRules',
  );
}

/** `services/dynamicRules/dynamicRules.ts:21-26`. */
export async function listRuleFields(signal?: AbortSignal) {
  return wire(
    ruleFieldListSchema,
    await creditApi.get<unknown>('/ProductRules/fields', withSignal(signal)),
    'GET /ProductRules/fields',
  );
}

/** `governmentDepartment.tsx:7-10`; `governmentDepartmentContext.tsx:22-25` manda só searchString e tenant. */
export async function listGovernmentDepartments(
  params: { searchString?: string | undefined; tenant: string | undefined },
  signal?: AbortSignal,
) {
  return wire(
    governmentDepartmentPageSchema,
    await creditApi.get<unknown>('/GovernmentDepartment', {
      params: { searchString: params.searchString, tenant: params.tenant },
      ...withSignal(signal),
    }),
    'GET /GovernmentDepartment',
  );
}

/** `creditProductTemplate.ts:24-36`: sem tenant. */
export async function createSignatureTemplate(productId: string, body: TemplateBody) {
  return wire(
    signatureTemplateSchema.partial({ name: true }),
    await creditApi.post(`/SignatureDocumentTemplate/${productId}`, body),
    'POST /SignatureDocumentTemplate/{id}',
  );
}
